import React, { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useGetSmartFitResourceAvailablityHoursOptionsByTotalHours } from '~/modules/resourcing/hooks';
import { AllocationDistributionMode, AllocationPeriodType } from '~/types';
import { roundToDecimals } from '~/modules/resourcing/common/util/scheduleUtil';
import { ResourceOverAllocationConfirmationDialog } from '~/modules/resourcing/common/components/ResourceOverAllocationConfirmationDialog';
import ResourceAssignmentSmartFitConfirmationDialog from '../ResourceAssignmentSmartFitConfirmationDialog';

const checkHasEnoughAvailableHours = ({
  smartFitmentDetails,
  totalAllocatedHours,
  periodType
}) => {
  if (periodType !== AllocationPeriodType.EntireRange) {
    return !smartFitmentDetails.hasOverAllocatedPeriods;
  }

  return (
    roundToDecimals(totalAllocatedHours) ===
    roundToDecimals(smartFitmentDetails.availableHoursWhatFits || 0)
  );
};

const ShowSmartFitDialogOrLoading2 = ({
  confirmButtonLabel,
  periodType = AllocationPeriodType.EntireRange,
  resourceUser,
  totalAllocatedHours,
  allocationDateRange,
  projectUri,
  createAllocationWithUserAndOverrideRules,
  closeShowSmartFitDialogOrLoading,
  scale
}) => {
  const {
    isLoading,
    smartFitmentDetails,
    error
  } = useGetSmartFitResourceAvailablityHoursOptionsByTotalHours({
    userUri: resourceUser.id,
    dateRange: allocationDateRange,
    hoursPerPeriod: totalAllocatedHours,
    periodType,
    projectUri
  });

  const handleCreateAllocation = useCallback(
    (_, distributionMode) =>
      createAllocationWithUserAndOverrideRules(undefined, distributionMode),
    [createAllocationWithUserAndOverrideRules]
  );

  const hasEnoughAvailableHours =
    smartFitmentDetails &&
    checkHasEnoughAvailableHours({
      smartFitmentDetails,
      totalAllocatedHours,
      periodType
    });

  useEffect(() => {
    if (isLoading) return;
    if (error) {
      closeShowSmartFitDialogOrLoading();

      return;
    }
    if (hasEnoughAvailableHours) {
      createAllocationWithUserAndOverrideRules(
        undefined,
        AllocationDistributionMode.FitsAvailableHours
      );
    }
  }, [
    isLoading,
    error,
    hasEnoughAvailableHours,
    createAllocationWithUserAndOverrideRules,
    closeShowSmartFitDialogOrLoading
  ]);

  if (isLoading || hasEnoughAvailableHours || error) return null;

  return periodType !== AllocationPeriodType.EntireRange ? (
    <ResourceOverAllocationConfirmationDialog
      confirmButtonLabel={confirmButtonLabel}
      onClose={closeShowSmartFitDialogOrLoading}
      onConfirm={handleCreateAllocation}
      user={resourceUser}
    />
  ) : (
    <ResourceAssignmentSmartFitConfirmationDialog
      confirmButtonLabel={confirmButtonLabel}
      smartFitmentDetails={smartFitmentDetails}
      scale={scale}
      resourceUser={resourceUser}
      createAllocationWithUserAndOverrideRules={handleCreateAllocation}
      closeShowSmartFitDialogOrLoading={closeShowSmartFitDialogOrLoading}
    />
  );
};

ShowSmartFitDialogOrLoading2.propTypes = {
  confirmButtonLabel: PropTypes.string,
  periodType: PropTypes.string,
  resourceUser: PropTypes.object,
  totalAllocatedHours: PropTypes.number,
  allocationDateRange: PropTypes.object,
  projectUri: PropTypes.string,
  createAllocationWithUserAndOverrideRules: PropTypes.func,
  closeShowSmartFitDialogOrLoading: PropTypes.func,
  scale: PropTypes.string
};

export default ShowSmartFitDialogOrLoading2;
